import { FC, useState, useMemo, useEffect, useRef, createRef } from 'react';
import { Button, TextInput } from '../../';
import { StewardReportDetail, MemberSearchResult } from '../../../services/dtos';
import { WorkerCard } from './WorkerCard';
import { AddWorkerModal } from './AddWorkerModal';

interface WorkerAccordionListProps {
  details: StewardReportDetail[];
  weekDates: Date[];
  isReadOnly: boolean;
  jobId: string;
  addedMemberIds?: Set<string>; // Members that have been added but may not have detail records yet
  stewardReportId: number;
  onDaySave: (date: Date, uniqueMemberId: string, dayData: { regTimeHours?: number; overTimeHours?: number; doubleTimeHours?: number; showCallQty?: number; mealPenaltyQty?: number; regTimeRate?: number; showCallRate?: number; mealPenaltyRate?: number }) => Promise<void>;
  onDetailDelete: (stewardReportId: number, uniqueMemberId: string) => void;
  onWorkerAdd: (member: MemberSearchResult) => Promise<void>;
  onWorkerLocalStateChange?: (workerLocalStates: Map<string, { localValues: Map<string, string>; rates: { regTimeRate: number; showCallRate: number; mealPenaltyRate: number } }>) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

// Group details by uniqueMemberId
type WorkerDetailsGroup = {
  uniqueMemberId: string;
  details: StewardReportDetail[];
};

export const WorkerAccordionList: FC<WorkerAccordionListProps> = ({
  details,
  weekDates,
  isReadOnly,
  jobId,
  addedMemberIds = new Set(),
  stewardReportId,
  onDaySave,
  onDetailDelete,
  onWorkerAdd,
  onWorkerLocalStateChange,
  onUnsavedChangesChange,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allExpanded, setAllExpanded] = useState(false);
  // Map of uniqueMemberId -> memberName (for members added but not yet saved - they don't have details yet)
  const [addedMemberNames, setAddedMemberNames] = useState<Map<string, string>>(new Map());
  // Map of uniqueMemberId -> { localValues, rates } for summary calculation
  const [workerLocalStates, setWorkerLocalStates] = useState<Map<string, { localValues: Map<string, string>; rates: { regTimeRate: number; showCallRate: number; mealPenaltyRate: number } }>>(new Map());
  // Track unsaved changes from all workers
  const [workerUnsavedStates, setWorkerUnsavedStates] = useState<Map<string, boolean>>(new Map());
  // Map of uniqueMemberId -> ref for rates change callback
  const ratesChangeRefs = useRef<Map<string, React.MutableRefObject<(() => void) | null>>>(new Map());

  // Build member names map from detail records (memberName is now included in StewardReportDetail)
  const memberNames = useMemo(() => {
    const names = new Map<string, string>();
    details.forEach((detail) => {
      if (detail.memberName && !names.has(detail.uniqueMemberId)) {
        names.set(detail.uniqueMemberId, detail.memberName);
      }
    });
    // Also include names from addedMemberNames for members that haven't been saved yet
    addedMemberNames.forEach((name, id) => {
      if (!names.has(id)) {
        names.set(id, name);
      }
    });
    return names;
  }, [details, addedMemberNames]);

  // Group details by uniqueMemberId
  // Also include members that have been added but don't have detail records yet
  const workerGroups = useMemo(() => {
    const groups = new Map<string, StewardReportDetail[]>();
    
    // Add all members that have detail records
    details.forEach((detail) => {
      if (!groups.has(detail.uniqueMemberId)) {
        groups.set(detail.uniqueMemberId, []);
      }
      groups.get(detail.uniqueMemberId)!.push(detail);
    });

    // Add members that have been added but don't have detail records yet
    addedMemberIds.forEach((uniqueMemberId) => {
      if (!groups.has(uniqueMemberId)) {
        groups.set(uniqueMemberId, []); // Empty array - no details yet
      }
    });

    return Array.from(groups.entries()).map(([uniqueMemberId, details]) => ({
      uniqueMemberId,
      details,
    }));
  }, [details, addedMemberIds]);

  const filteredGroups = workerGroups.filter((group) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const memberName = memberNames.get(group.uniqueMemberId) || '';
    return memberName.toLowerCase().includes(term);
  });

  const handleAddWorker = async (member: MemberSearchResult) => {
    // Store the member name for members that haven't been saved yet (no detail records)
    const memberName = member.fullName;
    setAddedMemberNames((prev) => {
      const newMap = new Map(prev);
      newMap.set(member.uniqueMemberId, memberName);
      return newMap;
    });
    
    await onWorkerAdd(member);
    setShowAddModal(false);
  };

  const toggleExpandAll = () => {
    setAllExpanded(!allExpanded);
    // TODO: Implement expand/collapse all functionality
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <Button
            onClick={() => setShowAddModal(true)}
            disabled={isReadOnly}
            size="sm"
          >
            Add Worker
          </Button>
          {!isReadOnly && details.length > 0 && (
            <>
              <Button
                color="gray"
                size="sm"
                onClick={toggleExpandAll}
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            </>
          )}
        </div>

        {details.length > 0 && (
          <div className="flex-1 max-w-xs">
            <TextInput
              type="text"
              placeholder="Search workers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Workers List */}
      {filteredGroups.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {searchTerm
              ? 'No workers match your search.'
              : 'No workers added yet. Click "Add Worker" to get started.'}
          </p>
          {!isReadOnly && !searchTerm && (
            <Button onClick={() => setShowAddModal(true)}>Add Worker</Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredGroups.map((group) => {
            // Use the first detail as the representative, or create a placeholder if no details exist yet
            const representativeDetail = group.details[0] || {
              id: 0,
              stewardReportId: 0,
              uniqueMemberId: group.uniqueMemberId,
              dateFor: weekDates[0]?.toISOString().split('T')[0] || '',
              regTimeHours: 0,
              overTimeHours: 0,
              doubleTimeHours: 0,
              showCallQty: 0,
              mealPenaltyQty: 0,
              regTimeRate: 0,
              showCallRate: 0,
              mealPenaltyRate: 0,
            } as StewardReportDetail;
            const memberName = memberNames.get(group.uniqueMemberId) || '';
            // Get or create ref for this worker's rates change callback
            if (!ratesChangeRefs.current.has(group.uniqueMemberId)) {
              ratesChangeRefs.current.set(group.uniqueMemberId, { current: null });
            }
            const ratesChangeRef = ratesChangeRefs.current.get(group.uniqueMemberId)!;
            
            return (
              <WorkerCard
                key={group.uniqueMemberId}
                detail={representativeDetail}
                allDetails={group.details}
                weekDates={weekDates}
                isReadOnly={isReadOnly}
                memberName={memberName}
                uniqueMemberId={group.uniqueMemberId}
                onDaySave={(date, uniqueMemberId, dayData) => onDaySave(date, uniqueMemberId, dayData)}
                onDelete={() => {
                  // Delete all details for this worker using stewardReportId and uniqueMemberId
                  onDetailDelete(stewardReportId, group.uniqueMemberId);
                }}
                onRatesChangeRef={ratesChangeRef}
                onLocalStateChange={(localValues, rates) => {
                  // Update worker local state
                  setWorkerLocalStates((prev) => {
                    const newMap = new Map(prev);
                    if (localValues.size > 0 && rates.regTimeRate > 0) {
                      newMap.set(group.uniqueMemberId, { localValues, rates });
                    } else {
                      newMap.delete(group.uniqueMemberId);
                    }
                    return newMap;
                  });
                }}
                onUnsavedChange={(hasUnsaved) => {
                  // Update worker unsaved state
                  setWorkerUnsavedStates((prev) => {
                    const newMap = new Map(prev);
                    if (hasUnsaved) {
                      newMap.set(group.uniqueMemberId, true);
                    } else {
                      newMap.delete(group.uniqueMemberId);
                    }
                    return newMap;
                  });
                }}
              />
            );
          })}
        </div>
      )}

      {/* Add Worker Modal */}
      <AddWorkerModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelect={handleAddWorker}
        jobId={jobId}
      />
      
      {/* Notify parent when worker local states change */}
      {onWorkerLocalStateChange && (
        <WorkerLocalStateNotifier
          workerLocalStates={workerLocalStates}
          onNotify={onWorkerLocalStateChange}
        />
      )}
      
      {/* Notify parent when unsaved changes state changes */}
      {onUnsavedChangesChange && (
        <UnsavedChangesNotifier
          workerUnsavedStates={workerUnsavedStates}
          onNotify={onUnsavedChangesChange}
        />
      )}
    </div>
  );
};

// Helper component to notify parent when worker local states change
const WorkerLocalStateNotifier: FC<{
  workerLocalStates: Map<string, { localValues: Map<string, string>; rates: { regTimeRate: number; showCallRate: number; mealPenaltyRate: number } }>;
  onNotify: (states: Map<string, { localValues: Map<string, string>; rates: { regTimeRate: number; showCallRate: number; mealPenaltyRate: number } }>) => void;
}> = ({ workerLocalStates, onNotify }) => {
  useEffect(() => {
    onNotify(workerLocalStates);
  }, [workerLocalStates, onNotify]);
  return null;
};

// Helper component to notify parent when unsaved changes state changes
const UnsavedChangesNotifier: FC<{
  workerUnsavedStates: Map<string, boolean>;
  onNotify: (hasUnsaved: boolean) => void;
}> = ({ workerUnsavedStates, onNotify }) => {
  useEffect(() => {
    const hasUnsaved = workerUnsavedStates.size > 0;
    onNotify(hasUnsaved);
  }, [workerUnsavedStates, onNotify]);
  return null;
};

